<template>
  <view class="c-money" :style="compStyle">
    <slot name="prefix" />

    <text v-if="symbol" class="c-money__symbol" :style="symbolStyle">{{ symbol }}</text>
    <text class="c-money__main" :style="mainStyle">{{ computeContentValue[0] }}</text>
    <text class="c-money__decimal" :style="decimalStyle" v-if="computeContentValue[1]"
      >.{{ computeContentValue[1] }}</text
    >

    <text
      v-if="!!computeLineThroughValue && computeLineThroughValue !== value"
      class="c-moeny__line-through"
      :style="_lineThroughStyle"
    >
      ¥{{ computeLineThroughValue }}
    </text>

    <slot />
  </view>
</template>

<script setup lang="ts">
import { businessUtils } from '@/utils';
import { computed, toRefs } from 'vue';

interface Props {
  /** 尺寸大小 - [整数位，单位｜小数位，小数位] */
  size: number[];
  /** 数值 */
  value: string | number;
  /** 前置单位 */
  symbol?: string;
  /** 字体颜色 */
  color?: string;
  /** 使用粗体 */
  bold?: boolean;
  /** 是否使用自定义字体 */
  useFont?: boolean;
  /** 是否划线 */
  lineThrough?: boolean;
  /** 是否内联块样式 */
  inlineBlock?: boolean;
  /** 是否分转元 */
  autoConvert?: boolean;
  /** 外边距 */
  margins?: number[];
  /** 划线价配置 */
  lineThroughConfig?: {
    /** 划线价 */
    value?: string | number;
    /** 划线价距离左侧金额的间距 */
    leftSpacing?: number;
    /** 划线价字体大小；默认跟随金额字体大小 */
    size?: number;
    /** 划线价字体 十六进制颜色 */
    color?: string;
    /** 划线价样式 */
    style?: AnyObject;
  };
}

const props = withDefaults(defineProps<Props>(), {
  symbol: '¥',
  bold: false,
  useFont: true,
  lineThroughConfig: () => ({
    value: 0,
    size: 0,
    color: '',
    leftSpacing: 0,
    style: {},
  }),
  lineThrough: false,
  inlineBlock: false,
  autoConvert: true,
  margins: () => [],
});
const {
  size,
  value,
  symbol,
  color,
  bold,
  useFont,
  lineThrough,
  inlineBlock,
  autoConvert,
  margins,
  lineThroughConfig,
} = toRefs(props);

/** 组件样式 */
const compStyle = computed(() => {
  let style = {};

  if (color?.value) {
    style = { ...style, color: color?.value };
  }

  if (useFont.value) {
    // 粗体样式
    if (bold.value) {
      style = { ...style, 'font-family': 'din-bold' };
    } else {
      style = { ...style, 'font-family': 'din' };
    }
  } else {
    if (bold.value) {
      style = { ...style, 'font-weight': 'bold' };
    }
    style = { ...style, 'font-family': 'auto' };
  }

  if (lineThrough.value) {
    style = { ...style, 'text-decoration': 'line-through' };
  }

  if (inlineBlock.value) {
    style = { ...style, display: 'inline-block' };
  }

  if (margins.value.length) {
    const mv = margins.value;
    style = { ...style, margin: `${mv[0]}rpx ${mv[1]}rpx ${mv[2]}rpx ${mv[3]}rpx` };
  }

  return style;
});

/** 符号样式 */
const symbolStyle = computed(() => {
  return { 'font-size': `${size.value[1] || size.value[0]}rpx` };
});

/** 主金额样式 */
const mainStyle = computed(() => {
  return { 'font-size': `${size.value[0]}rpx` };
});

/** 小数样式 */
const decimalStyle = computed(() => {
  return {
    'font-size': `${size.value[2] || size.value[1] || size.value[0]}rpx`,
  };
});

/** 划线价样式 */
const _lineThroughStyle = computed(() => {
  const lv = lineThroughConfig.value;

  let style: AnyObject = {
    ...lv.style,
    'font-size': `${size.value[1] || size.value[2]}rpx`,
  };

  if (lv.size) {
    style = {
      ...style,
      'font-size': `${lv.size}rpx`,
    };
  }

  if (lv.color) {
    style = {
      ...style,
      color: `${lv.color}rpx`,
    };
  }

  if (lv.leftSpacing) {
    style = {
      ...style,
      'margin-left': `${lv.leftSpacing}rpx`,
    };
  }

  return style;
});

/** 价格内容 */
const computeContentValue = computed(() => {
  const r = autoConvert.value ? 100 : 1;
  return businessUtils.price.roundedUp(+value.value / r, true).split('.');
});

/** 划线价 */
const computeLineThroughValue = computed(() => {
  const ltVal = lineThroughConfig.value.value;
  if (!ltVal) return;
  return businessUtils.price.roundedUp(+ltVal / 100, true);
});
</script>

<style scoped lang="scss">
.c-money {
  position: relative;

  .c-moeny__line-through {
    color: $N5;
    text-decoration: line-through;
    font-family: auto;
    font-weight: normal;
  }
}
</style>
